/** Conversión geodésica WGS84 → UTM para captura web. Devuelve null para coordenadas
 * inválidas o fuera de la extensión UTM convencional (80°S–84°N). */
export interface UtmPoint { zone: number; hemisphere: 'N' | 'S'; easting: number; northing: number; }
export function wgs84ToUtm(lat: number, lon: number): UtmPoint | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -80 || lat > 84 || lon < -180 || lon > 180 || (lat === 0 && lon === 0)) return null;
  let zone = Math.min(60, Math.max(1, Math.floor((lon + 180) / 6) + 1));
  // Ajustes convencionales de zona: Noruega y Svalbard.
  if (lat >= 56 && lat < 64 && lon >= 3 && lon < 12) zone = 32;
  if (lat >= 72 && lat < 84) {
    if (lon >= 0 && lon < 9) zone = 31;
    else if (lon >= 9 && lon < 21) zone = 33;
    else if (lon >= 21 && lon < 33) zone = 35;
    else if (lon >= 33 && lon < 42) zone = 37;
  }
  const a = 6378137; const e2 = 0.0066943799901413165; const k0 = 0.9996;
  const phi = lat * Math.PI / 180;
  const lambda = lon * Math.PI / 180;
  const l0 = ((zone - 1) * 6 - 180 + 3) * Math.PI / 180;
  const ep2 = e2 / (1 - e2); const sin = Math.sin(phi); const cos = Math.cos(phi);
  const N = a / Math.sqrt(1 - e2 * sin * sin);
  const T = Math.tan(phi) ** 2; const C = ep2 * cos ** 2; const A = cos * (lambda - l0);
  const M = a * ((1 - e2 / 4 - 3 * e2 ** 2 / 64 - 5 * e2 ** 3 / 256) * phi
    - (3 * e2 / 8 + 3 * e2 ** 2 / 32 + 45 * e2 ** 3 / 1024) * Math.sin(2 * phi)
    + (15 * e2 ** 2 / 256 + 45 * e2 ** 3 / 1024) * Math.sin(4 * phi)
    - (35 * e2 ** 3 / 3072) * Math.sin(6 * phi));
  const easting = k0 * N * (A + (1 - T + C) * A ** 3 / 6
    + (5 - 18 * T + T ** 2 + 72 * C - 58 * ep2) * A ** 5 / 120) + 500000;
  let northing = k0 * (M + N * Math.tan(phi) * (A ** 2 / 2
    + (5 - T + 9 * C + 4 * C ** 2) * A ** 4 / 24
    + (61 - 58 * T + T ** 2 + 600 * C - 330 * ep2) * A ** 6 / 720));
  if (lat < 0) northing += 10000000;
  return { zone, hemisphere: lat < 0 ? 'S' : 'N', easting: Math.round(easting * 100) / 100, northing: Math.round(northing * 100) / 100 };
}
